// TelcoTrack — parser CSV parcel trasy (PARCELY_KN_DEF + PARCELY_KN_P / LENGTH).
// Středník, uvozovky, WKT s mezerami, české desetinné čárky v LENGTH.

export interface OwnerLookupCsvRow {
  /** Join klíč z CSV sloupce ID. */
  joinId: string;
  kuKod: string;
  parcelaLabel: string;
  /** Běžné metry z LENGTH (součet při duplicitním ID). */
  lengthM: number | null;
  count: number | null;
  parVymera: number | null;
  druhPozemkuKod: string;
  zpusobVyuzitiKod: string;
  budId: string;
  stavParc: string;
  obecKod: string;
  /** Druhý identifikátor z CSV (ID_2). */
  joinId2: string;
}

export interface OwnerLookupCsvJoinResult {
  parcels: OwnerLookupCsvRow[];
  /** ID z LENGTH, která nejsou v DEF — ignorují se, nejdou do KN. */
  unmatchedLengthIds: string[];
  /** ID z DEF bez odpovídajícího LENGTH (metry zůstanou prázdné). */
  unmatchedDefIds: string[];
  defRowCount: number;
  lengthRowCount: number;
  /** Řádky LENGTH, které se trefilo na nějakou DEF parcelu. */
  matchedLengthRowCount: number;
  /** Řádky LENGTH bez shody — ignorují se. */
  unmatchedLengthRowCount: number;
  uniqueParcelCount: number;
}

export interface OwnerLookupMeterPatch {
  lengthM: number | null;
  count: number | null;
  obecKod: string;
}

function normalizeHeader(raw: string): string {
  return raw
    .replace(/^\uFEFF/, '')
    .trim()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_|_$/g, '');
}

/** Středníkové CSV s uvozovkami (WKT `Point (x y)` i `LENGTH` s čárkou). */
export function parseSemicolonCsv(text: string): string[][] {
  const src = String(text ?? '').replace(/^\uFEFF/, '');
  const rows: string[][] = [];
  let row: string[] = [];
  let field = '';
  let inQuotes = false;

  for (let i = 0; i < src.length; i++) {
    const c = src[i];
    if (inQuotes) {
      if (c === '"') {
        if (src[i + 1] === '"') {
          field += '"';
          i += 1;
        } else {
          inQuotes = false;
        }
      } else {
        field += c;
      }
      continue;
    }
    if (c === '"') {
      inQuotes = true;
      continue;
    }
    if (c === ';') {
      row.push(field);
      field = '';
      continue;
    }
    if (c === '\n') {
      row.push(field);
      if (row.some((cell) => cell.trim() !== '')) rows.push(row);
      row = [];
      field = '';
      continue;
    }
    if (c === '\r') {
      continue;
    }
    field += c;
  }
  if (field.length > 0 || row.length > 0) {
    row.push(field);
    if (row.some((cell) => cell.trim() !== '')) rows.push(row);
  }
  return rows;
}

/** `115,3980675` / `115.398` / `1 234,5` → number; prázdné → null. */
export function parseCzechDecimal(value: string | null | undefined): number | null {
  if (value == null) return null;
  const cleaned = String(value)
    .trim()
    .replace(/\s+/g, '')
    .replace(/^[\"']|[\"']$/g, '');
  if (!cleaned) return null;
  const normalized = cleaned.replace(',', '.');
  const n = Number.parseFloat(normalized);
  return Number.isFinite(n) ? n : null;
}

function pick(map: Record<string, string>, ...keys: string[]): string {
  for (const key of keys) {
    const v = map[key];
    if (v != null && String(v).trim() !== '') return String(v).trim();
  }
  return '';
}

/** Excel číslo `501` / `501.0` / `1.33e+9` → stabilní ID string; desetinné LENGTH nechat. */
export function stringifyOwnerLookupCell(cell: unknown): string {
  if (cell == null) return '';
  if (typeof cell === 'number' && Number.isFinite(cell)) {
    if (Math.abs(cell - Math.round(cell)) < 1e-9) return String(Math.round(cell));
    return String(cell);
  }
  return String(cell).trim();
}

/** Excel / SheetJS AOA → string matice (čísla LENGTH zůstanou čitelné pro parseCzechDecimal). */
export function stringifyOwnerLookupMatrix(rows: unknown[][] | null | undefined): string[][] {
  if (!rows?.length) return [];
  return rows
    .map((row) => (row ?? []).map((cell) => stringifyOwnerLookupCell(cell)))
    .filter((row) => row.some((cell) => cell !== ''));
}

/** `501` / `501.0` / `501 ` / scientific → porovnatelný join klíč. */
export function normalizeOwnerLookupJoinId(value: string | number | null | undefined): string {
  let s = stringifyOwnerLookupCell(value);
  if (!s) return '';
  if (/e[+-]?\d+/i.test(s)) {
    const n = Number(s);
    if (Number.isFinite(n) && Math.abs(n) < 1e16) s = String(Math.round(n));
  }
  return s.replace(/\.0+$/, '');
}

/** `st. 76/5` vs `st.76/5` vs mezery. */
export function normalizeOwnerLookupParcelaLabel(value: string | null | undefined): string {
  return String(value ?? '')
    .normalize('NFC')
    .replace(/\s+/g, ' ')
    .trim()
    .toLocaleLowerCase('cs')
    .replace(/\s*\.\s*/g, '.')
    .replace(/\s*\/\s*/g, '/');
}

function matrixToMaps(matrix: string[][]): Record<string, string>[] {
  if (!matrix.length) return [];
  const headers = matrix[0].map((h, idx) => normalizeHeader(h) || `col_${idx + 1}`);
  const rows: Record<string, string>[] = [];
  for (let i = 1; i < matrix.length; i++) {
    const cells = matrix[i] ?? [];
    if (cells.every((c) => !String(c ?? '').trim())) continue;
    const row: Record<string, string> = {};
    headers.forEach((header, idx) => {
      row[header] = String(cells[idx] ?? '').trim();
    });
    rows.push(row);
  }
  return rows;
}

function kuDigits(raw: string): string {
  const digits = raw.replace(/\D/g, '');
  if (digits.length >= 6) return digits.slice(0, 6);
  return digits;
}

function kuFromRow(row: Record<string, string>): string {
  return kuDigits(pick(row, 'katuze_kod', 'ku_kod', 'kod_ku'));
}

function parcelaFromRow(row: Record<string, string>): string {
  return pick(row, 'text_km', 'parcela', 'parcelni_cislo', 'par_cis');
}

function joinIdFromRow(row: Record<string, string>): string {
  return normalizeOwnerLookupJoinId(pick(row, 'id', 'def_id'));
}

function joinId2FromRow(row: Record<string, string>): string {
  return normalizeOwnerLookupJoinId(pick(row, 'id_2'));
}

function sumLengthFields(
  rows: Record<string, string>[],
): OwnerLookupMeterPatch {
  let lengthM: number | null = null;
  let count: number | null = null;
  let obecKod = '';
  for (const row of rows) {
    const nextLen = parseCzechDecimal(pick(row, 'length', 'delka', 'delka_m'));
    const nextCount = parseCzechDecimal(pick(row, 'count', 'pocet'));
    if (nextLen != null) lengthM = (lengthM ?? 0) + nextLen;
    if (nextCount != null) count = (count ?? 0) + nextCount;
    obecKod = obecKod || pick(row, 'obec_kod');
  }
  return { lengthM, count, obecKod };
}

/** LENGTH → metry podle sloupce ID (součet při více řádcích stejného ID). */
export function ownerLookupMetersByJoinId(
  lengthMatrix: string[][] | null | undefined,
): Map<string, OwnerLookupMeterPatch> {
  const meters = new Map<string, OwnerLookupMeterPatch>();
  const lengthMaps = lengthMatrix?.length ? matrixToMaps(lengthMatrix) : [];
  const lengthById = new Map<string, Record<string, string>[]>();
  for (const row of lengthMaps) {
    const id = joinIdFromRow(row);
    if (!id) continue;
    const list = lengthById.get(id) ?? [];
    list.push(row);
    lengthById.set(id, list);
  }
  for (const [id, rows] of lengthById) {
    meters.set(id, sumLengthFields(rows));
  }
  return meters;
}

function parcelKey(kuKod: string, parcelaLabel: string): string {
  return `${kuKod}|${normalizeOwnerLookupParcelaLabel(parcelaLabel)}`;
}

function lengthDisplayId(row: Record<string, string>): string {
  return (
    joinIdFromRow(row) ||
    normalizeOwnerLookupJoinId(pick(row, 'slouceny_def_id')) ||
    joinId2FromRow(row)
  );
}

function lengthParcelKeys(row: Record<string, string>): string[] {
  const keys: string[] = [];
  const slKu = kuDigits(pick(row, 'slouceny_def_katuze_kod'));
  const slText = pick(row, 'slouceny_def_text_km');
  if (slKu && slText) keys.push(parcelKey(slKu, slText));
  const ku = kuFromRow(row);
  const text = parcelaFromRow(row);
  if (ku && text) keys.push(parcelKey(ku, text));
  return keys;
}

function matchLengthRowToDefKey(
  row: Record<string, string>,
  byId: Map<string, string>,
  byId2: Map<string, string>,
  parcels: Map<string, OwnerLookupCsvRow>,
): string | null {
  const lengthId = joinIdFromRow(row);
  if (lengthId && byId.has(lengthId)) return byId.get(lengthId) ?? null;
  const slDefId = normalizeOwnerLookupJoinId(pick(row, 'slouceny_def_id'));
  if (slDefId && byId.has(slDefId)) return byId.get(slDefId) ?? null;
  const id2 = joinId2FromRow(row);
  if (id2 && byId2.has(id2)) return byId2.get(id2) ?? null;
  for (const key of lengthParcelKeys(row)) {
    if (parcels.has(key)) return key;
  }
  return null;
}

function rowFromFields(
  row: Record<string, string>,
  extras: Partial<OwnerLookupCsvRow> = {},
): OwnerLookupCsvRow | null {
  const kuKod = kuFromRow(row);
  const parcelaLabel = parcelaFromRow(row);
  if (!kuKod || !parcelaLabel) return null;
  return {
    joinId: joinIdFromRow(row),
    kuKod,
    parcelaLabel,
    lengthM: parseCzechDecimal(pick(row, 'length', 'delka', 'delka_m')),
    count: parseCzechDecimal(pick(row, 'count', 'pocet')),
    parVymera: parseCzechDecimal(
      pick(row, 'par_vymera', 'slouceny_def_par_vymera', 'vymera'),
    ),
    druhPozemkuKod: pick(row, 'drupoz_kod', 'druh_pozemku'),
    zpusobVyuzitiKod: pick(row, 'zpvypa_kod', 'zpusob_vyuziti'),
    budId: pick(row, 'bud_id'),
    stavParc: pick(row, 'stav_parc', 'stav_parcely'),
    obecKod: pick(row, 'obec_kod'),
    joinId2: joinId2FromRow(row),
    ...extras,
  };
}

function mergeParcel(
  existing: OwnerLookupCsvRow,
  incoming: OwnerLookupCsvRow,
  sumMetrics: boolean,
): OwnerLookupCsvRow {
  const pickNum = (a: number | null, b: number | null): number | null => {
    if (sumMetrics) {
      if (a == null && b == null) return null;
      return (a ?? 0) + (b ?? 0);
    }
    return a ?? b;
  };
  return {
    joinId: existing.joinId || incoming.joinId,
    kuKod: existing.kuKod || incoming.kuKod,
    parcelaLabel: existing.parcelaLabel || incoming.parcelaLabel,
    lengthM: pickNum(existing.lengthM, incoming.lengthM),
    count: pickNum(existing.count, incoming.count),
    parVymera: existing.parVymera ?? incoming.parVymera,
    druhPozemkuKod: existing.druhPozemkuKod || incoming.druhPozemkuKod,
    zpusobVyuzitiKod: existing.zpusobVyuzitiKod || incoming.zpusobVyuzitiKod,
    budId: existing.budId || incoming.budId,
    stavParc: existing.stavParc || incoming.stavParc,
    obecKod: existing.obecKod || incoming.obecKod,
    joinId2: existing.joinId2 || incoming.joinId2,
  };
}

/**
 * DEF je jediná množina parcel (KN/ARES jen z KATUZE_KOD + TEXT_KM).
 * LENGTH jen doplní metry — pár: ID, Sloučený_DEF_ID, ID_2, nebo k.ú.+parcela.
 */
export function joinOwnerLookupTables(
  defMatrix: string[][] | null | undefined,
  lengthMatrix: string[][] | null | undefined,
): OwnerLookupCsvJoinResult {
  const defMaps = defMatrix?.length ? matrixToMaps(defMatrix) : [];
  const lengthMaps = lengthMatrix?.length ? matrixToMaps(lengthMatrix) : [];

  const merged = new Map<string, OwnerLookupCsvRow>();
  const byId = new Map<string, string>();
  const byId2 = new Map<string, string>();

  for (const row of defMaps) {
    const parcel = rowFromFields(row);
    if (!parcel) continue;
    const fromDef: OwnerLookupCsvRow = {
      ...parcel,
      lengthM: null,
      count: null,
    };
    const key = parcelKey(fromDef.kuKod, fromDef.parcelaLabel);
    const prev = merged.get(key);
    merged.set(key, prev ? mergeParcel(prev, fromDef, false) : fromDef);
    if (fromDef.joinId && !byId.has(fromDef.joinId)) byId.set(fromDef.joinId, key);
    if (fromDef.joinId2 && !byId2.has(fromDef.joinId2)) byId2.set(fromDef.joinId2, key);
  }

  const rowsByKey = new Map<string, Record<string, string>[]>();
  const unmatchedLengthIds: string[] = [];
  let matchedLengthRowCount = 0;
  let unmatchedLengthRowCount = 0;

  for (const row of lengthMaps) {
    const key = matchLengthRowToDefKey(row, byId, byId2, merged);
    if (!key) {
      unmatchedLengthRowCount += 1;
      const display = lengthDisplayId(row);
      if (display) unmatchedLengthIds.push(display);
      continue;
    }
    matchedLengthRowCount += 1;
    const list = rowsByKey.get(key) ?? [];
    list.push(row);
    rowsByKey.set(key, list);
  }

  const unmatchedDefIds: string[] = [];
  for (const [key, parcel] of merged) {
    const rows = rowsByKey.get(key);
    if (!rows?.length) {
      if (parcel.joinId) unmatchedDefIds.push(parcel.joinId);
      continue;
    }
    const patch = sumLengthFields(rows);
    merged.set(key, {
      ...parcel,
      lengthM: patch.lengthM,
      count: patch.count,
      obecKod: parcel.obecKod || patch.obecKod,
    });
  }

  const parcels = [...merged.values()].sort((a, b) => {
    const ku = a.kuKod.localeCompare(b.kuKod);
    if (ku !== 0) return ku;
    return a.parcelaLabel.localeCompare(b.parcelaLabel, 'cs');
  });

  return {
    parcels,
    unmatchedLengthIds: [...new Set(unmatchedLengthIds)].sort(),
    unmatchedDefIds: [...new Set(unmatchedDefIds)].sort(),
    defRowCount: defMaps.length,
    lengthRowCount: lengthMaps.length,
    matchedLengthRowCount,
    unmatchedLengthRowCount,
    uniqueParcelCount: parcels.length,
  };
}

export interface OwnerLookupMeterAttachable {
  joinId?: string | null;
  joinId2?: string | null;
  kuKod?: string | null;
  parcelaLabel?: string | null;
  lengthM?: number | null;
  count?: number | null;
  obecKod?: string | null;
}

/** Doplní LENGTH metry na už známé DEF/KN parcely. Extra LENGTH se ignoruje. */
export function attachOwnerLookupMeters<T extends OwnerLookupMeterAttachable>(
  parcels: T[],
  lengthMatrix: string[][] | null | undefined,
): { parcels: T[]; unmatchedLengthIds: string[]; unmatchedDefIds: string[]; unmatchedLengthRowCount: number } {
  const lengthMaps = lengthMatrix?.length ? matrixToMaps(lengthMatrix) : [];
  const byId = new Map<string, number>();
  const byId2 = new Map<string, number>();
  const byParcel = new Map<string, number>();
  parcels.forEach((parcel, idx) => {
    const id = normalizeOwnerLookupJoinId(parcel.joinId);
    if (id && !byId.has(id)) byId.set(id, idx);
    const id2 = normalizeOwnerLookupJoinId(parcel.joinId2);
    if (id2 && !byId2.has(id2)) byId2.set(id2, idx);
    if (parcel.kuKod && parcel.parcelaLabel) {
      const key = parcelKey(String(parcel.kuKod), String(parcel.parcelaLabel));
      if (!byParcel.has(key)) byParcel.set(key, idx);
    }
  });

  const rowsByIndex = new Map<number, Record<string, string>[]>();
  const unmatchedLengthIds: string[] = [];
  let unmatchedLengthRowCount = 0;

  for (const row of lengthMaps) {
    let idx: number | undefined;
    const lengthId = joinIdFromRow(row);
    if (lengthId && byId.has(lengthId)) idx = byId.get(lengthId);
    if (idx == null) {
      const slDefId = normalizeOwnerLookupJoinId(pick(row, 'slouceny_def_id'));
      if (slDefId && byId.has(slDefId)) idx = byId.get(slDefId);
    }
    if (idx == null) {
      const id2 = joinId2FromRow(row);
      if (id2 && byId2.has(id2)) idx = byId2.get(id2);
    }
    if (idx == null) {
      for (const key of lengthParcelKeys(row)) {
        if (byParcel.has(key)) {
          idx = byParcel.get(key);
          break;
        }
      }
    }
    if (idx == null) {
      unmatchedLengthRowCount += 1;
      const display = lengthDisplayId(row);
      if (display) unmatchedLengthIds.push(display);
      continue;
    }
    const list = rowsByIndex.get(idx) ?? [];
    list.push(row);
    rowsByIndex.set(idx, list);
  }

  const unmatchedDefIds: string[] = [];
  const next = parcels.map((parcel, idx) => {
    const rows = rowsByIndex.get(idx);
    if (!rows?.length) {
      const id = normalizeOwnerLookupJoinId(parcel.joinId);
      if (id) unmatchedDefIds.push(id);
      return parcel;
    }
    const patch = sumLengthFields(rows);
    return {
      ...parcel,
      lengthM: patch.lengthM,
      count: patch.count,
      obecKod: parcel.obecKod || patch.obecKod,
    };
  });
  return {
    parcels: next,
    unmatchedLengthIds: [...new Set(unmatchedLengthIds)].sort(),
    unmatchedDefIds: [...new Set(unmatchedDefIds)].sort(),
    unmatchedLengthRowCount,
  };
}

/**
 * Spojí DEF (množina parcel) a volitelný LENGTH (metry).
 * Bez DEF nevzniknou žádné parcely — extra LENGTH řádky se jen vypíšou jako ignorované.
 */
export function joinOwnerLookupCsvs(
  defCsvText: string | null | undefined,
  lengthCsvText: string | null | undefined,
): OwnerLookupCsvJoinResult {
  return joinOwnerLookupTables(
    defCsvText ? parseSemicolonCsv(defCsvText) : null,
    lengthCsvText ? parseSemicolonCsv(lengthCsvText) : null,
  );
}
